/**
 * BuilderSchedule.
 *
 * Section 3 of the Recurring Builder drawer. Collects the schedule:
 * frequency, frequency-specific day selectors, start date, and how
 * the series ends.
 *
 * Frequency-specific controls:
 *   - weekly / biweekly -> day of week (7 small buttons)
 *   - monthly / quarterly / annually -> day of month (1-31 or "last")
 *   - daily -> no extra day control
 *
 * Date pickers use NovalaDatePicker (shared across the whole app).
 * Each trigger button has its own ref so the picker can anchor to it.
 */

import { useRef, useState, type CSSProperties } from "react";
import { Calendar } from "lucide-react";
import { BuilderSection } from "./RecurringBuilder";
import { NovalaDatePicker } from "../../../../components/NovalaDatePicker/NovalaDatePicker";
import {
  usesMonthDay,
  usesWeekDay,
} from "./builderUtils";
import { formatDateLong } from "../recurringUtils";
import type {
  BuilderDraft,
  BuilderValidationErrors,
  ScheduleEndType,
} from "./builderTypes";
import {
  WEEKDAY_LETTER,
  BUILDER_FREQUENCIES,
  FREQUENCY_PILL_LABELS,
} from "./builderTypes";
import type { RecurringFrequency } from "../recurringTypes";

export interface BuilderScheduleProps {
  draft: BuilderDraft;
  errors: BuilderValidationErrors;
  updateDraft: (partial: Partial<BuilderDraft>) => void;
}

export function BuilderSchedule({
  draft,
  errors,
  updateDraft,
}: BuilderScheduleProps) {
  const startRef = useRef<HTMLButtonElement>(null);
  const endRef = useRef<HTMLButtonElement>(null);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const handleFrequencyChange = (freq: RecurringFrequency) => {
    updateDraft({ frequency: freq });
  };

  const handleEndTypeChange = (endType: ScheduleEndType) => {
    updateDraft({ endType });
  };

  const handleDayOfMonthChange = (raw: string) => {
    if (raw === "last") {
      updateDraft({ dayOfMonth: "last" });
    } else {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n)) updateDraft({ dayOfMonth: n });
    }
  };

  return (
    <BuilderSection number={3} title="Schedule">
      {/* Frequency pills */}
      <div style={fieldStyle}>
        <div style={fieldLabelStyle}>Frequency</div>
        <div style={pillRowStyle}>
          {BUILDER_FREQUENCIES.map((freq) => {
            const selected = draft.frequency === freq;
            return (
              <button
                key={freq}
                type="button"
                onClick={() => handleFrequencyChange(freq)}
                aria-pressed={selected}
                style={{
                  ...pillStyle,
                  ...(selected ? pillSelectedStyle : {}),
                }}
              >
                {FREQUENCY_PILL_LABELS[freq]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Frequency-specific day selector + start date row */}
      <div style={fieldRowStyle}>
        {/* Day of week (weekly / biweekly) */}
        {usesWeekDay(draft.frequency) && (
          <div style={fieldStyle}>
            <div style={fieldLabelStyle}>Day of week</div>
            <div style={dowRowStyle}>
              {WEEKDAY_LETTER.map((letter, i) => {
                const selected = draft.dayOfWeek === i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => updateDraft({ dayOfWeek: i })}
                    aria-pressed={selected}
                    style={{
                      ...dowBtnStyle,
                      ...(selected ? dowBtnSelectedStyle : {}),
                    }}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>
            {errors.dayOfWeek && (
              <div style={errorTextStyle}>{errors.dayOfWeek}</div>
            )}
          </div>
        )}

        {/* Day of month (monthly / quarterly / annually) */}
        {usesMonthDay(draft.frequency) && (
          <div style={fieldStyle}>
            <label style={fieldLabelStyle} htmlFor="builder-dom">
              Day of month
            </label>
            <div style={selectWrapStyle}>
              <select
                id="builder-dom"
                value={draft.dayOfMonth === "last" ? "last" : String(draft.dayOfMonth)}
                onChange={(e) => handleDayOfMonthChange(e.target.value)}
                style={{
                  ...selectInputStyle,
                  ...(errors.dayOfMonth ? inputErrorStyle : {}),
                }}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {ordinal(d)}
                  </option>
                ))}
                <option value="last">Last day</option>
              </select>
            </div>
            {errors.dayOfMonth && (
              <div style={errorTextStyle}>{errors.dayOfMonth}</div>
            )}
          </div>
        )}

        {/* Start date */}
        <div style={fieldStyle}>
          <label style={fieldLabelStyle}>Start date</label>
          <button
            ref={startRef}
            type="button"
            onClick={() => setStartOpen(true)}
            style={{
              ...dateTriggerStyle,
              ...(errors.startDate ? inputErrorStyle : {}),
            }}
          >
            <Calendar size={15} style={{ color: "#3A4A52", flexShrink: 0 }} />
            {draft.startDate
              ? formatDateLong(draft.startDate)
              : "Pick a date..."}
          </button>
          {errors.startDate && (
            <div style={errorTextStyle}>{errors.startDate}</div>
          )}
        </div>
      </div>

      {/* Ends */}
      <div style={fieldStyle}>
        <div style={fieldLabelStyle}>Ends</div>
        <div style={endsOptsStyle}>
          {/* Never */}
          <label
            style={{
              ...endsOptStyle,
              ...(draft.endType === "never" ? endsOptSelectedStyle : {}),
            }}
          >
            <input
              type="radio"
              name="builder-ends"
              checked={draft.endType === "never"}
              onChange={() => handleEndTypeChange("never")}
              style={radioStyle}
            />
            <span style={endsOptLabelStyle}>Never</span>
          </label>

          {/* After N occurrences */}
          <label
            style={{
              ...endsOptStyle,
              ...(draft.endType === "after" ? endsOptSelectedStyle : {}),
            }}
          >
            <input
              type="radio"
              name="builder-ends"
              checked={draft.endType === "after"}
              onChange={() => handleEndTypeChange("after")}
              style={radioStyle}
            />
            <span style={endsOptLabelStyle}>After</span>
            <input
              type="number"
              min={1}
              max={999}
              value={draft.maxOccurrences}
              onChange={(e) =>
                updateDraft({ maxOccurrences: Number(e.target.value) })
              }
              onFocus={() => handleEndTypeChange("after")}
              style={{
                ...inlineNumberStyle,
                ...(errors.maxOccurrences ? inputErrorStyle : {}),
              }}
            />
            <span style={endsOptHintStyle}>occurrences</span>
          </label>

          {/* On date */}
          <label
            style={{
              ...endsOptStyle,
              ...(draft.endType === "on" ? endsOptSelectedStyle : {}),
            }}
          >
            <input
              type="radio"
              name="builder-ends"
              checked={draft.endType === "on"}
              onChange={() => handleEndTypeChange("on")}
              style={radioStyle}
            />
            <span style={endsOptLabelStyle}>On</span>
            <button
              ref={endRef}
              type="button"
              onClick={() => {
                handleEndTypeChange("on");
                setEndOpen(true);
              }}
              style={{
                ...inlineDateTriggerStyle,
                ...(errors.endDate ? inputErrorStyle : {}),
              }}
            >
              {draft.endDate ? formatDateLong(draft.endDate) : "pick a date"}
            </button>
          </label>
        </div>
        {errors.maxOccurrences && (
          <div style={errorTextStyle}>{errors.maxOccurrences}</div>
        )}
        {errors.endDate && <div style={errorTextStyle}>{errors.endDate}</div>}
      </div>

      {/* Date pickers (portal-mounted) */}
      <NovalaDatePicker
        isOpen={startOpen}
        anchorEl={startRef.current}
        value={draft.startDate}
        onChange={(iso) => {
          updateDraft({ startDate: iso });
          setStartOpen(false);
        }}
        onClose={() => setStartOpen(false)}
      />
      <NovalaDatePicker
        isOpen={endOpen}
        anchorEl={endRef.current}
        value={draft.endDate}
        onChange={(iso) => {
          updateDraft({ endDate: iso });
          setEndOpen(false);
        }}
        onClose={() => setEndOpen(false)}
        minDate={draft.startDate}
      />
    </BuilderSection>
  );
}

/** 1 -> "1st", 2 -> "2nd", 21 -> "21st". */
function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// ---------- Styles ----------

const fieldStyle: CSSProperties = {
  marginBottom: 14,
};

const fieldRowStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
  marginBottom: 14,
};

const fieldLabelStyle: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "#3A4A52",
  marginBottom: 5,
  letterSpacing: "0.02em",
};

const textInputBase: CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  border: "1px solid #D8DEE6",
  borderRadius: 7,
  fontSize: 13,
  background: "#ffffff",
  color: "#0E1A1A",
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
};

const selectInputStyle: CSSProperties = {
  ...textInputBase,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  paddingRight: 30,
  cursor: "pointer",
};

const selectWrapStyle: CSSProperties = {
  position: "relative",
};

const inputErrorStyle: CSSProperties = {
  borderColor: "#B42318",
};

const errorTextStyle: CSSProperties = {
  marginTop: 4,
  fontSize: 11.5,
  color: "#B42318",
};

const pillRowStyle: CSSProperties = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
};

const pillStyle: CSSProperties = {
  padding: "7px 14px",
  border: "1px solid #D8DEE6",
  borderRadius: 999,
  fontSize: 12,
  cursor: "pointer",
  background: "#ffffff",
  color: "#3A4A52",
  fontWeight: 500,
  fontFamily: "inherit",
};

const pillSelectedStyle: CSSProperties = {
  background: "#0E1A1A",
  color: "#ffffff",
  borderColor: "#0E1A1A",
  fontWeight: 600,
};

const dowRowStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: 4,
};

const dowBtnStyle: CSSProperties = {
  padding: "8px 0",
  border: "1px solid #D8DEE6",
  borderRadius: 6,
  background: "#ffffff",
  color: "#3A4A52",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  textAlign: "center",
  fontFamily: "inherit",
};

const dowBtnSelectedStyle: CSSProperties = {
  background: "#0E1A1A",
  color: "#ffffff",
  borderColor: "#0E1A1A",
  fontWeight: 700,
};

const dateTriggerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "9px 12px",
  border: "1px solid #D8DEE6",
  borderRadius: 7,
  background: "#ffffff",
  cursor: "pointer",
  fontSize: 13,
  color: "#0E1A1A",
  width: "100%",
  textAlign: "left",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const endsOptsStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const endsOptStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 12.5,
  color: "#263238",
};

const endsOptSelectedStyle: CSSProperties = {
  background: "#F3F5F5",
};

const radioStyle: CSSProperties = {
  width: 14,
  height: 14,
  margin: 0,
  accentColor: "#0E1A1A",
  cursor: "pointer",
  flexShrink: 0,
};

const endsOptLabelStyle: CSSProperties = {
  fontWeight: 500,
  color: "#0E1A1A",
};

const endsOptHintStyle: CSSProperties = {
  color: "#6B7684",
  fontSize: 12,
};

const inlineNumberStyle: CSSProperties = {
  width: 64,
  padding: "4px 8px",
  border: "1px solid #D8DEE6",
  borderRadius: 5,
  fontSize: 12,
  textAlign: "center",
  fontFamily: "ui-monospace, Menlo, monospace",
  background: "#ffffff",
  color: "#0E1A1A",
  outline: "none",
  boxSizing: "border-box",
};

const inlineDateTriggerStyle: CSSProperties = {
  padding: "5px 10px",
  border: "1px solid #D8DEE6",
  borderRadius: 5,
  background: "#ffffff",
  cursor: "pointer",
  fontSize: 12,
  color: "#0E1A1A",
  fontFamily: "inherit",
  minWidth: 120,
  textAlign: "left",
};

export default BuilderSchedule;